import React, { useContext, useMemo } from 'react';
import { PrismicImage } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Colors from '@components/shared/Colors';
import { CartItemType, NavStatus, PageSlice, ProductCode } from '@contracts';
import PlusIcon from '@images/vms-account/vms-icons/FilledPlus.svg';
import TickIcon from '@images/vms-account/vms-icons/Tick.svg';
import { createCartItem } from '@utils/cart';
import useProductPrices from '@utils/useProductPrices';
import Button from './Button';
import Text from './Text';
import { BodyLG } from './TextTypes';

interface Props {
  slice: PageSlice;
}

export const ProductComparisonTable: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const { navDispatch } = useContext(NavContext);
  const { currency } = state;
  const rowHeadingText = slice?.primary?.row_title?.text;
  const highlightedColIndex = slice?.primary?.highlighted_column_index;
  const tableHeadingText = slice?.primary?.table_header_text?.text;
  const tableData = slice?.primary?.table_json?.text ? JSON.parse(slice?.primary?.table_json?.text) : [];
  const productCodes = useMemo(() => tableData.map((item: any) => item.productCode), []);
  const productPrices = useProductPrices(productCodes);
  const explainerText = slice?.primary?.explainer_text?.richText;
  const explainerImage = slice?.primary?.explainer_image;
  const explainerBackground = slice?.primary?.explainer_background_color;
  const rowSubTitle = slice?.primary?.row_sub_title;
  const hasPricing = Object.values(productPrices)[0];

  const getAllUniqueRows = () => {
    const allRows: string[] = [];

    tableData.forEach((item) => {
      allRows.push(...Object.keys(item.rows));
    });

    const uniqueRows = [...new Set(allRows)];

    uniqueRows.sort((rowA, rowB) => {
      const countA = tableData.filter((item) => item.rows[rowA]).length;
      const countB = tableData.filter((item) => item.rows[rowB]).length;

      // Prioritize lesser occurrence
      if (countA < countB) {
        return -1;
      }
      if (countA > countB) {
        return 1;
      }

      // If occurrence is the same, keep original order
      return uniqueRows.indexOf(rowA) - uniqueRows.indexOf(rowB);
    });

    return uniqueRows;
  };

  const handleAddToCart = (productCode: ProductCode) => () => {
    const price = Number(productPrices[productCode]);
    if (price < 0) return;
    const cartItem = createCartItem(productCode, 1, price, true, CartItemType.OneOffPurchase, undefined, undefined);
    dispatch({
      type: 'cartItemAdd',
      data: cartItem
    });
    navDispatch({ type: 'setStatus', data: NavStatus.Basket });
  };

  const uniqueRows = getAllUniqueRows();

  return (
    <Container>
      <h2>{t(rowHeadingText)}</h2>
      {rowSubTitle ? (
        <RowSubTitleWrapper>
          <BodyLG>{t(rowSubTitle)}</BodyLG>
        </RowSubTitleWrapper>
      ) : null}
      <InnerContainer>
        <Table>
          <thead>
            <TableHeaderRow>
              <TableCell>{tableHeadingText}</TableCell>
              {tableData.map((row, index) => (
                <TableCell key={index} className={index === highlightedColIndex ? 'highlight highlight-th th-cell' : 'th-cell'}>
                  {row.name}
                </TableCell>
              ))}
            </TableHeaderRow>
          </thead>
          <tbody>
            {uniqueRows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row}</TableCell>
                {tableData.map((item, index) => (
                  <TableCell key={item.name} className={index === highlightedColIndex ? 'highlight' : ''}>
                    {item.rows[row] ? <TickIcon /> : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {hasPricing ? (
              <TableRow>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{t('components.price')}</TableCell>
                {tableData.map((item, index) => (
                  <TableCell key={index} className={index === highlightedColIndex ? 'highlight price-cell' : 'price-cell'}>
                    <PriceWrap>
                      <AddToCartButton onClick={handleAddToCart(item.productCode)}>
                        <PlusIcon />
                        <span>
                          {item.productCode && productPrices[item.productCode] !== undefined
                            ? `${currency.symbol}${productPrices[item.productCode]}`
                            : '...'}
                        </span>
                      </AddToCartButton>
                    </PriceWrap>
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </tbody>
        </Table>
        {explainerText ? (
          <ExplainerColumn $background={explainerBackground}>
            <PrismicImage field={explainerImage} loading="lazy" />
            <Text richText={explainerText} text="" textAlign="center" textColor={Colors.navyBlue} />
          </ExplainerColumn>
        ) : null}
      </InnerContainer>
    </Container>
  );
};

export default ProductComparisonTable;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  align-self: stretch;
  width: 100%;
  padding: 60px 0;

  @media (max-width: 1200px) {
    padding: 50px 0;
  }

  & > h2 {
    font-family: 'Domaine';
    font-size: 30px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    color: ${Colors.navyBlue};
    padding: 0 30px;
    text-align: center;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  gap: 40px;
  width: 100%;
  max-width: 1120px;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
    padding: 0 30px;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  border: 1px solid ${Colors.ecru};
  margin-bottom: 0;

  @media (max-width: 1200px) {
    max-width: 800px;
  }
`;

const TableRow = styled.tr`
  background-color: ${Colors.cream};

  &:nth-child(even) {
    background-color: ${Colors.ecru50};
  }
`;

const TableHeaderRow = styled.tr`
  background: ${Colors.ecru};

  & td {
    font-weight: bold;
  }
`;

const TableCell = styled.td`
  border-width: 0;
  padding: 15px;
  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  color: ${Colors.navyBlue};
  text-align: center;
  width: 160px;

  @media (max-width: 1200px) {
    font-size: 12px;
    padding: 12px 15px;
  }

  &.th-cell {
    line-height: 120%;
  }

  &.highlight {
    border-left: 2px solid ${Colors.gold};
    border-right: 2px solid ${Colors.gold};

    &:last-child {
      border-right-width: 2px;
    }

    &.price-cell {
      border-bottom: 2px solid ${Colors.gold};
    }
  }

  &.highlight-th {
    border-top: 2px solid ${Colors.gold};
    position: relative;

    &::before {
      content: 'SAVE 35%';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate3D(-50%, -50%, 0);
      text-align: center;
      font-family: 'BrownStd';
      font-size: 10px;
      font-style: normal;
      font-weight: 700;
      line-height: 100%; /* 10px */
      letter-spacing: 0.5px;
      text-transform: uppercase;
      padding: 3px 5px;
      background: ${Colors.lilac};
      border-radius: 2px;
      color: ${Colors.cream};
      white-space: nowrap;
    }
  }

  &:first-child {
    padding-left: 15px;
    text-align: left;
    width: auto;
  }

  &:last-child {
    padding-right: 15px;
  }
`;

const ExplainerColumn = styled.div<{ $background?: string }>`
  display: flex;
  padding: 40px 25px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px;
  max-width: 375px;
  width: 100%;
  border-radius: 5px;
  background: ${(props) => (props.$background ? props.$background : Colors.babyBlue)};

  @media (max-width: 1200px) {
    border-radius: 10px;
  }

  & section > div {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const RowSubTitleWrapper = styled.div`
  margin-top: -8px;
  padding: 0 30px;
  text-align: center;
`;

const AddToCartButton = styled(Button)`
  background: transparent;
  padding: 0;
  height: auto;
  display: flex;
  flex-direction: row;
  gap: 5px;

  &:hover {
    background: transparent;

    & > svg path {
      fill: ${Colors.navyBlue};
    }
  }

  span {
    font-family: 'Domaine';
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 110%;
    text-align: center;
    color: ${Colors.navyBlue};
    letter-spacing: 0;

    @media (max-width: 1200px) {
      font-size: 16px;
    }
  }

  & > svg path {
    fill: ${Colors.lilac};
    transition: all 0.2s ease;
  }
`;

const PriceWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 5px;

  & > svg {
    width: 25px;
    height: 25px;
  }
`;
